import { motion, useInView } from 'framer-motion';
import { Coffee, Mail, MapPin, Phone, Send, User } from 'lucide-react';
import { useRef, useState } from 'react';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.3, delayChildren: 0.1 } }
};

const boxVariants = {
  hidden: { opacity: 0, y: 40, scale: 0.95 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.6, ease: 'easeOut' } }
};

const GetInTouch = () => {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, amount: 0.2 });

  const [formData, setFormData] = useState({ name: '', email: '', message: '' });

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = () => {
    if (!formData.name || !formData.email || !formData.message) {
      alert('Please fill in all required fields');
      return;
    }
    const form = document.createElement('form');
    form.action = 'https://formspree.io/f/xyzwebbv';
    form.method = 'POST';
    form.style.display = 'none';

    const nameInput = document.createElement('input');
    nameInput.name = 'name';
    nameInput.value = formData.name;
    form.appendChild(nameInput);

    const emailInput = document.createElement('input');
    emailInput.name = 'email';
    emailInput.value = formData.email;
    form.appendChild(emailInput);

    const messageInput = document.createElement('textarea');
    messageInput.name = 'message';
    messageInput.value = formData.message;
    form.appendChild(messageInput);

    document.body.appendChild(form);
    form.submit();
  };

  const contactInfo = [
    { icon: <Mail className="w-6 h-6" />, label: 'Email', value: 'hugowiratama21@gmail.com', color: 'text-blue-400' },
    { icon: <MapPin className="w-6 h-6" />, label: 'Location', value: 'Bekasi, Indonesia', color: 'text-green-400' },
    { icon: <Phone className="w-6 h-6" />, label: 'Phone', value: '(+62) 8121945****', color: 'text-purple-400' }
  ];

  return (
    <section
      id="contact"
      className="relative min-h-screen px-6 py-20 overflow-hidden text-white bg-gradient-to-tr from-gray-900 via-slate-900 to-gray-900"
    >
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute bg-blue-500 rounded-full top-20 left-20 w-72 h-72 blur-3xl" />
        <div className="absolute bg-purple-500 rounded-full bottom-20 right-20 w-96 h-96 blur-3xl" />
      </div>

      {/* Grid Pattern */}
      <div className="absolute inset-0 bg-grid-pattern bg-grid opacity-[0.02]" />

      <div className="relative z-10 mx-auto max-w-7xl">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: -30 }}
          transition={{ duration: 0.8 }}
          className="mb-16 text-center"
        >
          <h2 className="text-4xl font-bold text-transparent md:text-5xl bg-gradient-to-r from-blue-400 via-purple-400 to-blue-400 bg-clip-text">
            Get In Touch
          </h2>
          <p className="mt-4 text-lg text-gray-400">Let's collaborate and bring your ideas to life</p>
        </motion.div>

        {/* Contact Info Cards */}
        <motion.div
          ref={containerRef}
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          className="grid max-w-4xl gap-6 mx-auto mb-16 md:grid-cols-3"
        >
          {contactInfo.map((info, idx) => (
            <motion.div
              key={idx}
              variants={boxVariants}
              whileHover={{ scale: 1.05, y: -5 }}
              className="p-6 text-center border shadow-lg bg-gray-800/30 backdrop-blur-sm border-gray-700/50 rounded-xl"
            >
              <div className={`${info.color} mb-4 flex justify-center`}>{info.icon}</div>
              <h4 className="mb-2 font-semibold text-white">{info.label}</h4>
              <p className="text-sm text-gray-300">{info.value}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Contact Form */}
        <motion.div
          variants={boxVariants}
          className="p-8 border shadow-2xl bg-gray-800/50 backdrop-blur-sm border-gray-700/50 rounded-2xl md:p-12"
        >
          <div className="mb-10 text-center">
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="p-3 rounded-lg bg-blue-600/20">
                <Send className="w-8 h-8 text-blue-400" />
              </div>
              <h3 className="text-3xl font-bold text-blue-400">Send Message</h3>
            </div>
            <p className="max-w-2xl mx-auto text-lg leading-relaxed text-gray-300">
              Ready to start a project or just want to chat? Drop me a message and I'll get back to you as soon as possible.
            </p>
          </div>

          <div className="grid gap-8 mb-8 md:grid-cols-2">
            {/* Name */}
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.1 }}>
              <label className="block mb-3 text-sm font-semibold text-gray-300">Your Name *</label>
              <div className="relative">
                <User className="absolute w-5 h-5 text-gray-400 transform -translate-y-1/2 left-4 top-1/2" />
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter your name"
                  required
                  className="w-full py-4 pl-12 pr-4 text-white placeholder-gray-400 border bg-gray-700/50 border-gray-600/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </motion.div>

            {/* Email */}
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.2 }}>
              <label className="block mb-3 text-sm font-semibold text-gray-300">Your Email *</label>
              <div className="relative">
                <Mail className="absolute w-5 h-5 text-gray-400 transform -translate-y-1/2 left-4 top-1/2" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter your email"
                  required
                  className="w-full py-4 pl-12 pr-4 text-white placeholder-gray-400 border bg-gray-700/50 border-gray-600/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </motion.div>
          </div>

          {/* Message */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.3 }} className="mb-8">
            <label className="block mb-3 text-sm font-semibold text-gray-300">Your Message *</label>
            <textarea
              name="message"
              rows="6"
              value={formData.message}
              onChange={handleInputChange}
              placeholder="Tell me about your project, ask a question, or just say hello!"
              required
              className="w-full px-4 py-4 text-white placeholder-gray-400 border resize-none bg-gray-700/50 border-gray-600/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </motion.div>

          {/* Action Buttons */}
          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            <motion.button
              type="button"
              onClick={handleSubmit}
              whileHover={{ scale: 1.05, boxShadow: "0 20px 25px -5px rgba(59, 130, 246, 0.3)" }}
              whileTap={{ scale: 0.95 }}
              className="flex items-center gap-3 px-8 py-4 font-semibold shadow-lg bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 rounded-xl"
            >
              <Send className="w-5 h-5" /> Send Message
            </motion.button>

            <motion.a
              href="https://saweria.co/hugowrtma"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05, boxShadow: "0 20px 25px -5px rgba(139, 69, 19, 0.3)" }}
              whileTap={{ scale: 0.95 }}
              className="flex items-center gap-3 px-8 py-4 font-semibold text-white shadow-lg bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 rounded-xl"
            >
              <Coffee className="w-5 h-5" /> Buy me a coffee
            </motion.a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default GetInTouch;
